import {
  MedicalPlanForEnrollment,
  OnboardingPeriodBenefitsDto,
  PolicyType,
  SelfEnrollType,
} from '@zorro/clients';
import { getWhoIsEnrolled, showErrorNotification } from '@zorro/web/ui-utils';

import {
  PlanElectionItem,
  PlanElectionItemFormFields,
} from '../../PlanElectionForm';

export const getDefaultMedicalPlanFormFields = (
  selectedWhoIsEnrolled: string[],
  planId?: string | null,
  planName?: string | null,
  carrier?: string,
  premium?: number,
  selfEnroll?: SelfEnrollType,
  isCombinedPlanStructure?: boolean,
  policyType?: PolicyType | null,
  extendedPlanDetails?: string | null
): PlanElectionItemFormFields => {
  const sharedDefaultFields = {
    whoIsEnrolled: selectedWhoIsEnrolled,
    planId: planId || '',
    planName: planName || '',
    carrier: carrier || '',
    premium: premium || 0,
    isSelfEnroll: selfEnroll === SelfEnrollType.SELF_ENROLL,
    isMedicareCoverage: false,
    medicareProvider: '',
    planType: policyType || PolicyType.IFP,
    combinedPlanInfo: extendedPlanDetails ?? '',
    isCombinedPlanStructure: isCombinedPlanStructure ?? false,
  };
  if (isCombinedPlanStructure) {
    return {
      ...sharedDefaultFields,
      planType: policyType || PolicyType.MEDICARE_IFP,
    };
  }
  if (policyType === PolicyType.MEDICARE) {
    return {
      ...sharedDefaultFields,
      planId: planId || 'MEDICARE',
      carrier: '',
      isMedicareCoverage: true,
      medicareProvider: carrier || '',
      planType: policyType || PolicyType.MEDICARE,
    };
  }
  return {
    ...sharedDefaultFields,
  };
};

export const mapMedicalPlanToPlanElectionItem = (
  medicalPlans: MedicalPlanForEnrollment[] | undefined
): PlanElectionItem[] | undefined => {
  return medicalPlans?.map((medicalPlan) => ({
    id: medicalPlan.id,
    carrierName: medicalPlan.carrierName,
    name: medicalPlan.name,
    premium: medicalPlan.premium,
    hsaEligible: medicalPlan.hsaEligible,
    deductible: medicalPlan.deductible,
    maxOutOfPocket: medicalPlan.maxOutOfPocket,
    benefitsSummaryUrl: medicalPlan.benefitsSummaryUrl,
    planMarket: medicalPlan.planMarket,
    selfEnroll: medicalPlan.selfEnroll,
    policyType: medicalPlan.policyType,
    extendedPlanDetails: medicalPlan.extendedPlanDetails,
  }));
};

export const validateMedicalPlanEnrollmentSelectedEnrolled = (
  benefits: OnboardingPeriodBenefitsDto | undefined,
  selectedEnrolled: Set<string>,
  insuredIdToNameMap: Map<string, string>
): boolean => {
  if (benefits?.dentalBenefits?.length) {
    const enrolledDentalIds = benefits.dentalBenefits.flatMap(
      ({ insuredPeople }) =>
        insuredPeople ? getWhoIsEnrolled(insuredPeople) : []
    );

    for (const enrolledDentalId of enrolledDentalIds) {
      if (!selectedEnrolled.has(enrolledDentalId)) {
        showErrorNotification({
          message: `${insuredIdToNameMap.get(enrolledDentalId)} cannot be removed from the medical plan while covered on a dental plan. Remove them from the supplemental plan first.`,
        });
        return false;
      }
    }
  }

  if (benefits?.visionBenefits?.length) {
    const enrolledVisionIds = benefits.visionBenefits.flatMap(
      ({ insuredPeople }) =>
        insuredPeople ? getWhoIsEnrolled(insuredPeople) : []
    );

    for (const enrolledVisionId of enrolledVisionIds) {
      if (!selectedEnrolled.has(enrolledVisionId)) {
        showErrorNotification({
          message: `${insuredIdToNameMap.get(enrolledVisionId)} cannot be removed from the medical plan while covered on a vision plan. Remove them from the supplemental plan first.`,
        });
        return false;
      }
    }
  }

  return true;
};
